import type { Article } from '~/types'
import { VideoType as VideoTypeEnum } from '~/enums/video-type'

export interface VideoProps {
  url: Maybe<string>
  uuid: Maybe<string>
}

export const useVideoArticle = (video: MaybeRef<Article.Video>) => {
  const videoType = computed<Article.VideoType>(() => unref(video).videoType)
  const id = computed<number>(() => unref(video).id)
  const title = computed<Maybe<string>>(() => unref(video).title)
  const wmtVideoUuid = computed<Maybe<string>>(() => unref(video).wmtVideoUuid)
  const description = computed<Maybe<string>>(() => unref(video).description)
  const youtubeUrl = computed<Maybe<string>>(() => unref(video).youtubeUrl)
  const facebookUrl = computed<Maybe<string>>(() => unref(video).facebookUrl)
  const hlsUrl = computed<Maybe<string>>(() => unref(video).hlsUrl)
  const { handleException } = useHandleException()

  const ifFacebookVideo = computed<boolean>(() => videoType.value === VideoTypeEnum.Facebook)
  const ifHlsVideo = computed<boolean>(() => videoType.value === VideoTypeEnum.Hls)
  const ifWmtVideo = computed<boolean>(() => videoType.value === VideoTypeEnum.WmtVideo)
  const ifYoutubeVideo = computed<boolean>(() => videoType.value === VideoTypeEnum.Youtube)

  const props = computed<VideoProps>(() => {
    return {
      url: facebookUrl.value || hlsUrl.value || youtubeUrl.value,
      uuid: wmtVideoUuid.value,
      isAutoplay: true,
    }
  })

  const component = computed<Maybe<string>>(() => {
    switch (videoType.value) {
      case VideoTypeEnum.WmtVideo:
        return 'WmtVideoEmbed'
      case VideoTypeEnum.Youtube:
        return 'YoutubeVideoEmbed'
      case VideoTypeEnum.Facebook:
        return 'FacebookVideoEmbed'
      case VideoTypeEnum.Hls:
        return 'StreamVideoEmbed'
      default:
        handleException(new Error(`Unable to resolve component for video type ${videoType.value}`))
        return null
    }
  })

  return {
    component,
    description,
    facebookUrl,
    hlsUrl,
    id,
    ifFacebookVideo,
    ifHlsVideo,
    ifWmtVideo,
    ifYoutubeVideo,
    props,
    title,
    videoType,
    wmtVideoUuid,
    youtubeUrl,
  }
}
